import { useEffect, useRef } from "react";
import { CHALLENGE_SIZES, esOrigenBackend } from "@/data/auth3dsStore";

/**
 * Step-Up Challenge (Paso 4) · Sprint 59 · F1B.
 *
 * iframe VISIBLE que hace POST del accessToken (JWT) a la stepUpUrl (host ya
 * validado por el backend). El tamaño se deriva del valor oficial
 * challengeWindowSize. Al terminar, el ACS redirige a nuestra returnUrl dentro
 * del iframe; esa página notifica al padre por `postMessage`. Aquí SÓLO se
 * aceptan mensajes cuyo origen sea EXACTAMENTE el backend, cuya fuente sea el
 * iframe del challenge, y con la forma esperada; cualquier otro se ignora. El
 * nonce NO viaja en el mensaje (lo conserva el orquestador en memoria).
 * Protección anti doble challenge y timeout. No registra el accessToken.
 */

const STEPUP_TIMEOUT_MS = 6 * 60 * 1000; // margen dentro de la reserva.

export const StepUpChallenge = ({
  stepUpUrl,
  accessToken,
  challengeWindowSize = "02",
  onReturned,
  onTimeout,
}) => {
  const iframeRef = useRef(null);
  const formRef = useRef(null);
  const enviadoRef = useRef(false);
  const finalizadoRef = useRef(false);

  const [w, h] = CHALLENGE_SIZES[challengeWindowSize] || CHALLENGE_SIZES["02"];

  useEffect(() => {
    if (!stepUpUrl || !accessToken) return undefined;

    const terminar = (payload) => {
      if (finalizadoRef.current) return;
      finalizadoRef.current = true;
      window.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
      if (payload === "timeout") onTimeout?.();
      else onReturned?.(payload);
    };

    const onMessage = (event) => {
      // Origen EXACTO del backend + fuente = el iframe del challenge.
      if (!esOrigenBackend(event.origin)) return;
      if (iframeRef.current && event.source !== iframeRef.current.contentWindow) return;
      const d = event.data;
      if (!d || d.source !== "ct-3ds" || d.type !== "challenge-returned") return;
      terminar({ status: d.status });
    };

    window.addEventListener("message", onMessage);
    let timer = setTimeout(() => terminar("timeout"), STEPUP_TIMEOUT_MS);

    // Anti doble challenge: un solo submit.
    if (!enviadoRef.current && formRef.current) {
      enviadoRef.current = true;
      try {
        formRef.current.submit();
      } catch {
        /* jsdom / cross-origin: el submit real ocurre en el navegador. */
      }
    }

    return () => {
      window.removeEventListener("message", onMessage);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepUpUrl, accessToken]);

  return (
    <section
      data-testid="stepup-container"
      className="rounded-2xl border hairline bg-card p-4 shadow-soft"
    >
      <p className="text-sm text-muted-foreground mb-3" role="status" aria-live="polite">
        Tu banco solicita una verificación adicional. Completa el reto para continuar.
      </p>
      <div className="w-full flex justify-center">
        <iframe
          title="cardinal-stepup"
          name="cs-stepup-iframe"
          ref={iframeRef}
          data-testid="stepup-iframe"
          width={w}
          height={h}
          style={{ border: 0, maxWidth: "100%" }}
        />
      </div>
      <form ref={formRef} data-testid="stepup-form" method="POST" action={stepUpUrl} target="cs-stepup-iframe">
        <input type="hidden" name="JWT" value={accessToken} readOnly />
      </form>
    </section>
  );
};

export default StepUpChallenge;
